import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    Users,
    Home,
    Image,
    Calendar,
    CalendarDays,
    History,
    Star,
    FileText,
    FileSignature,
    MessageSquare,
    Languages,
    LogOut,
    ChevronLeft,
    ChevronRight,
    Menu,
    X,
    Contact,
    Mail,
    LayoutDashboard
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserData } from '../../hooks';
import './cms-layout.css';

const CMSLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const { logout, isAuthenticated, isLoading: authLoading } = useAuth();
    const { pages, isLoading } = useUserData();

    // Check if we're in development environment
    const isDev = import.meta.env.DEV || window.location.hostname === 'localhost';
    const sidebarBgColor = isDev ? '#1a2332' : '#1e293b'; // Darker blue with slight tint for dev
    const mobileHeaderBgColor = isDev ? '#fff5e6' : '#ffffff'; // Light orange tint for dev

    // Detect mobile screen size - sidebar behavior at 920px or below
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 920;
            setIsMobile(mobile);
            // Auto-close sidebar on mobile/tablet only
            if (mobile) {
                setIsSidebarOpen(false);
            }
            // On desktop, sidebar state is controlled by user (no auto-open)
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Close sidebar when navigating on mobile
    useEffect(() => {
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    }, [location.pathname, isMobile]);

    const allMenuItems = [
        { path: '/cms', label: 'Dashboard', icon: LayoutDashboard, permission: 'dashboard' },
        { path: '/cms/users', label: 'Users', icon: Users, permission: 'users' },
        { path: '/cms/houses', label: 'Houses', icon: Home, permission: 'houses' },
        { path: '/cms/photos', label: 'Photos', icon: Image, permission: 'photos' },
        { path: '/cms/reservations', label: 'Reservations', icon: Calendar, permission: 'reservations' },
        { path: '/cms/ical', label: 'iCal', icon: CalendarDays, permission: 'ical' },
        { path: '/cms/reservation-history', label: 'History', icon: History, permission: 'reservationHistory' },
        { path: '/cms/standard-messages', label: 'Messages', icon: MessageSquare, permission: 'standardMessages' },
        { path: '/cms/translations', label: 'Translations', icon: Languages, permission: 'translations' },
        { path: '/cms/feedbacks', label: 'Feedbacks', icon: Star, permission: 'feedbacks' },
        { path: '/cms/blog', label: 'Blog', icon: FileText, permission: 'blog' },
        { path: '/cms/e-sign', label: 'E-Sign', icon: FileSignature, permission: 'eSign' },
        { path: '/cms/contact-requests', label: 'Contact Requests', icon: Mail, permission: 'contactRequests' },
        { path: '/cms/contacts', label: 'Partners', icon: Contact, permission: 'contacts' }
    ];

    // Filter menu items based on permissions (show all while loading)
    const menuItems = isLoading || !pages
        ? allMenuItems
        : allMenuItems.filter(item => pages[item.permission as keyof typeof pages]);

    const handleLogout = async () => {
        // Prevent multiple clicks
        if (isLoggingOut) return;

        try {
            setIsLoggingOut(true);
            // Set flag to prevent API interceptor from redirecting during logout
            sessionStorage.setItem('isLoggingOut', 'true');

            // Wait for logout to complete (tokens revoked, state cleared)
            await logout();

            // Force redirect to landing page
            window.location.replace('/');
        } catch (error) {
            console.error('Logout failed:', error);
            sessionStorage.removeItem('isLoggingOut');
            setIsLoggingOut(false);
            // Still redirect even on error
            window.location.replace('/');
        }
    };

    const isActive = (path: string) => {
        if (path === '/cms') {
            return location.pathname === '/cms';
        }
        return location.pathname.startsWith(path);
    };

    // Block render until auth is resolved — show nothing to avoid CMS flash
    if (authLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#f5f7fa',
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid #e2e8f0',
                    borderTop: '3px solid #d89d37',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // Not authenticated — redirect to login without rendering any CMS content
    // But don't redirect if user is actively logging out (they'll go to index)
    if (!isAuthenticated && !isLoggingOut) {
        window.location.replace('/login');
        return null;
    }

    // Get current page title based on route
    const getCurrentPageTitle = () => {
        const path = location.pathname;
        const menuItem = allMenuItems.find(item => {
            if (item.path === '/cms') {
                return path === '/cms';
            }
            return path.startsWith(item.path);
        });
        return menuItem?.label || 'CMS';
    };

    return (
        <div className="d-flex" style={{ minHeight: '100vh', backgroundColor: '#f5f7fa', overflow: 'hidden' }}>
            {/* Mobile Header Bar */}
            {isMobile && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '60px',
                        backgroundColor: mobileHeaderBgColor,
                        borderBottom: isDev ? '3px solid #ff6b00' : '1px solid #e5e7eb',
                        borderTop: isDev ? '3px solid #ff6b00' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 16px',
                        zIndex: 1049,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                    }}
                >
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="btn btn-link"
                        style={{
                            padding: '8px',
                            color: '#1e293b',
                            marginRight: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '40px',
                            minHeight: '40px'
                        }}
                    >
                        <Menu size={24} />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h6 className="mb-0 fw-semibold" style={{ fontSize: '1.1rem', color: '#1e293b' }}>
                            {getCurrentPageTitle()}
                        </h6>
                        {isDev && (
                            <span style={{
                                backgroundColor: '#ff6b00',
                                color: '#fff',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                padding: '3px 8px',
                                borderRadius: '4px',
                                letterSpacing: '0.5px'
                            }}>
                                DEV
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Overlay for mobile */}
            {isMobile && isSidebarOpen && (
                <div
                    onClick={() => setIsSidebarOpen(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1049,
                    }}
                />
            )}

            {/* Sidebar */}
            <div
                className="sidebar"
                style={{
                    width: '260px',
                    backgroundColor: sidebarBgColor,
                    borderRight: isDev ? '4px solid #ff6b00' : 'none',
                    color: 'white',
                    transition: 'all 0.3s ease',
                    position: 'fixed',
                    height: '100vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    zIndex: 1050,
                    boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
                    transform: isMobile && !isSidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
                }}
            >
                {/* Logo/Brand */}
                <div
                    className="d-flex align-items-center justify-content-between p-3"
                    style={{
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        height: '70px'
                    }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#d89d37',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '1.2rem'
                            }}
                        >
                            P
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '1.1rem' }}>Pure Key</h5>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <small style={{ opacity: 0.7, fontSize: '0.75rem' }}>CMS Panel</small>
                                {isDev && (
                                    <span style={{
                                        backgroundColor: '#ff6b00',
                                        color: '#fff',
                                        fontSize: '0.65rem',
                                        fontWeight: 'bold',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        letterSpacing: '0.5px'
                                    }}>
                                        DEV
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    {isMobile && (
                        <button
                            onClick={() => setIsSidebarOpen(false)}
                            className="btn btn-link text-white p-2"
                            style={{
                                fontSize: '1rem',
                                opacity: 0.8,
                                borderRadius: '8px'
                            }}
                        >
                            <X size={24} />
                        </button>
                    )}
                </div>

                {/* Menu Items */}
                <nav className="py-3">
                    {menuItems.map((item, index) => {
                        const IconComponent = item.icon;
                        return (
                            <Link
                                key={index}
                                to={item.path}
                                className="text-decoration-none"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '12px 20px',
                                    color: 'white',
                                    margin: '4px 12px',
                                    borderRadius: '10px',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: isActive(item.path) ? '#d89d37' : 'transparent',
                                    justifyContent: 'flex-start',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive(item.path)) {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive(item.path)) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                <IconComponent
                                    size={20}
                                    style={{
                                        minWidth: '20px',
                                        opacity: isActive(item.path) ? 1 : 0.8
                                    }}
                                />
                                <span
                                    className="ms-3 fw-medium"
                                    style={{
                                        fontSize: '0.9rem',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {item.label}
                                </span>
                            </Link>
                        );
                    })}

                    {/* Logout */}
                    <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <button
                            onClick={handleLogout}
                            className="btn w-100"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 20px',
                                color: 'white',
                                margin: '0 12px',
                                borderRadius: '10px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                transition: 'all 0.2s ease',
                                justifyContent: 'flex-start',
                                width: 'calc(100% - 24px)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#dc3545';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <LogOut size={20} style={{ minWidth: '20px', opacity: 0.8 }} />
                            <span className="ms-3 fw-medium" style={{ fontSize: '0.9rem' }}>
                                Logout
                            </span>
                        </button>
                    </div>
                </nav>
            </div>

            {/* Main Content */}
            <div
                className="flex-fill"
                style={{
                    marginLeft: isMobile ? '0' : '260px',
                    transition: 'all 0.3s ease',
                    width: '100%',
                    maxWidth: '100vw',
                    overflow: 'hidden'
                }}
            >
                {/* Page Content */}
                <div style={{
                    padding: isMobile ? '76px 16px 16px 16px' : '32px',
                    maxWidth: '100%',
                    overflowX: 'hidden'
                }}>
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default CMSLayout;